import axios from 'axios';

// Smart API URL detection - works for localhost and production without breaking styling
const getBaseURL = () => {
  // Check if we're in the browser
  if (typeof window !== 'undefined') {
    // Local development
    if (window.location.hostname === 'localhost') {
      return 'http://localhost:5000/api';
    }
    // Production - use relative path (Netlify proxy handles it)
    return '/api';
  }
  return 'http://localhost:5000/api';
};

const API_BASE_URL = getBaseURL();

// Track pending requests to deduplicate
const pendingRequests = new Map();

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Request interceptor - deduplicate identical requests
api.interceptors.request.use((config) => {
  const requestKey = `${config.method}_${config.url}`;
  
  if (pendingRequests.has(requestKey)) {
    const cancelSource = axios.CancelToken.source();
    config.cancelToken = cancelSource.token;
    cancelSource.cancel('Duplicate request cancelled');
    return Promise.reject({ message: 'Duplicate request', isDuplicate: true });
  }
  
  pendingRequests.set(requestKey, true);
  
  const cleanup = () => {
    pendingRequests.delete(requestKey);
  };
  
  config.cleanup = cleanup;
  return config;
});

// Response interceptor
api.interceptors.response.use(
  (response) => {
    if (response.config.cleanup) {
      response.config.cleanup();
    }
    return response;
  },
  (error) => {
    if (error.config && error.config.cleanup) {
      error.config.cleanup();
    }
    
    if (error.isDuplicate) {
      return Promise.reject(error);
    }
    
    if (error.response?.status === 401) {
      if (!window.location.pathname.startsWith('/admin') && 
          !window.location.pathname.includes('/login') && 
          !window.location.pathname.includes('/register')) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Admin API instance
const getAdminBaseURL = () => {
  if (typeof window !== 'undefined') {
    if (window.location.hostname === 'localhost') {
      return 'http://localhost:5000/admin';
    }
    return '/admin';
  }
  return 'http://localhost:5000/admin';
};

export const adminApi = axios.create({
  baseURL: getAdminBaseURL(),
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});

// Auth endpoints
export const register = (data) => api.post('/auth/register', data);
export const login = (data) => api.post('/auth/login', data);
export const logout = () => api.post('/auth/logout');
export const getMe = () => api.get('/auth/me');
export const verifyPhone = (data) => api.post('/auth/verify-phone', data);
export const resendOTP = (phone) => api.post('/auth/resend-otp', { phone });

// KYC endpoints
export const submitKYC = (formData) => api.post('/kyc/submit', formData, {
  headers: { 'Content-Type': 'multipart/form-data' }
});
export const getKYCStatus = () => api.get('/kyc/status');
export const resubmitKYC = (formData) => api.post('/kyc/resubmit', formData, {
  headers: { 'Content-Type': 'multipart/form-data' }
});

// Deposit endpoints
export const getDepositAddress = (chain = 'bsc') => api.get(`/deposit/address?chain=${chain}`);
export const getDeposits = () => api.get('/deposit');
export const getDepositById = (id) => api.get(`/deposit/${id}`);

// Withdrawal endpoints
export const requestWithdrawal = (data) => api.post('/withdrawals', data);
export const getWithdrawals = () => api.get('/withdrawals');
export const getWithdrawalById = (id) => api.get(`/withdrawals/${id}`);

// User endpoints
export const getBalance = () => api.get('/account/balance');
export const getAccountHistory = () => api.get('/account/history');
export const getAllocations = () => api.get('/account/allocations');

// Admin endpoints
export const adminLogin = (data) => adminApi.post('/auth/login', data);
export const adminVerify2FA = (data) => adminApi.post('/auth/verify-2fa', data);
export const getPendingKYC = () => adminApi.get('/kyc/pending');
export const approveKYC = (userId) => adminApi.post(`/kyc/${userId}/approve`);
export const rejectKYC = (userId, reason) => adminApi.post(`/kyc/${userId}/reject`, { reason });
export const getPendingWithdrawals = () => adminApi.get('/withdrawals/pending');
export const approveWithdrawal = (id) => adminApi.post(`/withdrawals/${id}/approve`);
export const rejectWithdrawal = (id, reason) => adminApi.post(`/withdrawals/${id}/reject`, { reason });
export const createTrade = (data) => adminApi.post('/trades/manual', data);
export const getAllUsers = () => adminApi.get('/users');
export const freezeUser = (userId) => adminApi.post(`/users/${userId}/freeze`);
export const unfreezeUser = (userId) => adminApi.post(`/users/${userId}/unfreeze`);
export const getAdminStats = () => adminApi.get('/stats');
export const getBlacklist = () => adminApi.get('/blacklist');
export const addToBlacklist = (address, reason) => adminApi.post('/blacklist', { address, reason });
export const removeFromBlacklist = (address) => adminApi.delete(`/blacklist/${address}`);
export const getAuditLogs = () => adminApi.get('/audit-logs');
export const initiateSweep = (depositIds) => adminApi.post('/sweeps/initiate', { depositIds });
export const getTreasuryBalance = () => adminApi.get('/treasury/balance');

export default api;